import React from "react";
import { Button, Card, CardBody, CardTitle, CardText } from "reactstrap";
import * as Sentry from "@sentry/browser";

/**
 * ErrorBoundary - Catches JavaScript errors anywhere in the child component tree
 * Prevents the entire app from crashing and shows a fallback UI instead
 *
 * @component
 * @example
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
		};
	}

	static getDerivedStateFromError(error) {
		// Update state so the next render will show the fallback UI
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		// Log error to console in development
		if (process.env.NODE_ENV === "development") {
			console.error("ErrorBoundary caught an error:", error, errorInfo);
		}

		// Log to Sentry
		Sentry.captureException(error, {
			contexts: {
				react: {
					componentStack: errorInfo.componentStack,
				},
			},
		});

		// Store error details in state
		this.setState({
			error,
			errorInfo,
		});
	}

	handleReset = () => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
		});
	};

	handleReload = () => {
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			const { fallback, showDetails = false } = this.props;

			// Use custom fallback if provided
			if (fallback) {
				return typeof fallback === "function"
					? fallback(this.state.error, this.handleReset)
					: fallback;
			}

			// Default fallback UI
			return (
				<div className="error-boundary-container" style={{ padding: "2rem" }}>
					<Card className="border-danger">
						<CardBody>
							<CardTitle tag="h4" className="text-danger">
								<i className="nc-icon nc-alert-circle-i mr-2"></i>
								Something went wrong
							</CardTitle>
							<CardText>
								An unexpected error occurred. Don't worry - your work may still
								be saved. You can try to continue or reload the page.
							</CardText>

							{showDetails && this.state.error && (
								<div className="mt-3 p-3 bg-light border rounded">
									<strong>Error Details:</strong>
									<pre className="mt-2 mb-0" style={{ fontSize: "0.85rem" }}>
										{this.state.error.toString()}
									</pre>
								</div>
							)}

							<div className="mt-3 d-flex gap-2">
								<Button color="primary" onClick={this.handleReset}>
									Try Again
								</Button>
								<Button color="secondary" outline onClick={this.handleReload}>
									Reload Page
								</Button>
							</div>
						</CardBody>
					</Card>
				</div>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
